import React from "react";
import { SkeletonCard, useLoadPhase } from "../../../components/ui";
import { formatLongDate } from "../clientMapping";
import { useClientPlan } from "../hooks/useClientPlan";

/**
 * The strip above the meal plan list: the plan this client is on, how long it
 * runs, and when it ends.
 *
 * What is drawn depends on what the payment history says — see `currentPlanOf`,
 * which is where the three states come from. Only one of them is the banner the
 * design draws; the other two exist because a client between plans and a client
 * who has never had one are both things this strip has to be able to say, and
 * an empty teal box saying "Plan —" would say neither.
 *
 * It loads on its own rather than being handed a plan: the client record has
 * nothing about a subscription on it, and this is the tab that needs one.
 */

/** One labelled figure of the banner — the whole of what the active state is. */
const Field: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div className="flex items-baseline gap-2">
    <span className="text-[13px] text-ink-muted">{label}</span>
    <span className="text-[14px] font-medium text-ink">{value}</span>
  </div>
);

const shell =
  "flex-shrink-0 flex flex-wrap items-center gap-x-8 gap-y-2 rounded-md border px-5 sm:px-6 py-4";

/** Hairline grey on white while a plan is running; flatter grey once it is
 * not — the two states now differ only in fill. */
const LIVE = `${shell} justify-between border-[0.5px] border-[#CDCED7] bg-white`;
const IDLE = `${shell} justify-between border-line bg-[#FAFBFC]`;

const pill =
  "inline-flex whitespace-nowrap rounded-full px-2.5 py-1 text-[12px] font-medium";

/**
 * "13 days", the way the design writes what is left — and the last day of a
 * plan reads as "today" rather than as "0 days".
 */
const daysLeftLabel = (days: number): string => {
  if (days <= 0) return "today";
  return `${days} day${days === 1 ? "" : "s"}`;
};

interface ClientPlanBannerProps {
  /** The client's `public_id` — whose payment requests are read. */
  clientId: string;
}

const ClientPlanBanner: React.FC<ClientPlanBannerProps> = ({ clientId }) => {
  const { plan, status, error } = useClientPlan(clientId);

  // A banner that lands quickly goes straight from blank to filled: the
  // placeholders are only reached once the fetch has visibly stalled.
  const phase = useLoadPhase(status === "loading");

  if (status === "loading") {
    return (
      <>
        {/* Said as soon as the request goes out, whether or not anything is
            drawn for it: the shimmer is the shape of an answer rather than the
            answer, so it is hidden from screen readers and this line stands
            for it. */}
        <span role="status" className="sr-only">
          Loading this client’s plan…
        </span>
        {/* The banner as one block, at the height it will keep — so the table
            under it does not jump down the page when the plan lands. */}
        <SkeletonCard phase={phase} className="h-[53px] flex-shrink-0" />
      </>
    );
  }

  if (status === "failed") {
    return (
      <section className={IDLE}>
        <p className="text-[14px] text-ink-muted">
          {error ?? "We could not load this client’s plan."}
        </p>
      </section>
    );
  }

  // Never been on one. Said as what it is, with what starts one — a client can
  // hold meal plans without ever having been sold a subscription.
  if (plan.state === "none") {
    return (
      <section className={IDLE}>
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
          <span className={`${pill} bg-[#F1F4F6] text-ink-muted`}>
            No plan yet
          </span>
          <span className="text-[13px] text-ink-muted">
            A plan starts when a payment request against this client is paid.
          </span>
        </div>
      </section>
    );
  }

  // Between plans: the one that ran out most recently, and the day it did. A
  // next plan may already be bought and waiting to start — until it does, this
  // client is on nothing, which is what the tag says.
  if (plan.state === "expired") {
    return (
      <section className={IDLE}>
        <Field label="Plan" value={plan.name} />
        <Field label="Duration" value={plan.duration} />
        <Field label="Ended on" value={formatLongDate(plan.endsOn)} />
        <span className={`${pill} bg-[#FDECEC] text-[#D14343]`}>Expired</span>
      </section>
    );
  }

  return (
    <section className={LIVE}>
      <Field label="Plan" value={plan.name} />
      <Field label="Duration" value={plan.duration} />
      <Field
        label="Ends on"
        value={`${formatLongDate(plan.endsOn)}, ${daysLeftLabel(plan.daysLeft)}`}
      />
    </section>
  );
};

export default ClientPlanBanner;
